import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { catalogoApi, type ProdutoFilters } from './api';
import type {
  CategoriaInput,
  GrupoInput,
  ProdutoInput,
} from './types';

const QK = {
  categorias: (ativo?: boolean) => ['catalogo', 'categorias', { ativo }] as const,
  produtos: (filters: ProdutoFilters) => ['catalogo', 'produtos', filters] as const,
  produto: (id: string) => ['catalogo', 'produtos', id] as const,
  grupos: (ativo?: boolean) => ['catalogo', 'grupos', { ativo }] as const,
};

export function useCategorias(ativo?: boolean) {
  return useQuery({
    queryKey: QK.categorias(ativo),
    queryFn: () => catalogoApi.listCategorias(ativo),
  });
}

export function useCreateCategoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CategoriaInput) => catalogoApi.createCategoria(body),
    onSuccess: () => {
      toast.success('Categoria criada');
      qc.invalidateQueries({ queryKey: ['catalogo', 'categorias'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateCategoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CategoriaInput> }) =>
      catalogoApi.updateCategoria(id, body),
    onSuccess: () => {
      toast.success('Categoria atualizada');
      qc.invalidateQueries({ queryKey: ['catalogo', 'categorias'] });
      qc.invalidateQueries({ queryKey: ['catalogo', 'produtos'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteCategoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogoApi.deleteCategoria(id),
    onSuccess: () => {
      toast.success('Categoria removida');
      qc.invalidateQueries({ queryKey: ['catalogo', 'categorias'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ---------------------------------------------------------------------------

export function useProdutos(filters: ProdutoFilters = {}) {
  return useQuery({
    queryKey: QK.produtos(filters),
    queryFn: () => catalogoApi.listProdutos(filters),
  });
}

export function useCreateProduto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProdutoInput) => catalogoApi.createProduto(body),
    onSuccess: () => {
      toast.success('Produto criado');
      qc.invalidateQueries({ queryKey: ['catalogo', 'produtos'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateProduto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ProdutoInput> }) =>
      catalogoApi.updateProduto(id, body),
    onSuccess: () => {
      toast.success('Produto atualizado');
      qc.invalidateQueries({ queryKey: ['catalogo', 'produtos'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteProduto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogoApi.deleteProduto(id),
    onSuccess: () => {
      toast.success('Produto removido');
      qc.invalidateQueries({ queryKey: ['catalogo', 'produtos'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ---------------------------------------------------------------------------

export function useGrupos(ativo?: boolean) {
  return useQuery({
    queryKey: QK.grupos(ativo),
    queryFn: () => catalogoApi.listGrupos(ativo),
  });
}

export function useCreateGrupo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: GrupoInput) => catalogoApi.createGrupo(body),
    onSuccess: () => {
      toast.success('Grupo criado');
      qc.invalidateQueries({ queryKey: ['catalogo', 'grupos'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateGrupo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<GrupoInput> }) =>
      catalogoApi.updateGrupo(id, body),
    onSuccess: () => {
      toast.success('Grupo atualizado');
      qc.invalidateQueries({ queryKey: ['catalogo', 'grupos'] });
      qc.invalidateQueries({ queryKey: ['catalogo', 'produtos'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteGrupo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogoApi.deleteGrupo(id),
    onSuccess: () => {
      toast.success('Grupo removido');
      qc.invalidateQueries({ queryKey: ['catalogo', 'grupos'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
